import { FeatureKeys } from './../app.reducer';
import { AppointmentStatus, AppointmentType, IAppointment, IVehicle } from '@signal/asp-data-commons';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { BdcDashboardState, IPendingAppointmentState } from './bdcdashboard.reducer';
import * as fromAdapter from './bdcdashboard.adapter';
import { IDashboardVehicle } from '../../shared/models/dashboard.model';

/* NgRx Selectors */

/* To select the Dashboard feature state from the app state */
export const selectDashboardFeature = createFeatureSelector<BdcDashboardState>(FeatureKeys.BDCDASHBOARD);

/* ----------------- User Vehicles -------------------------- */
/* To select the user vehicles loading flag in the dashboard state */
export const selectUserVehiclesLoading = createSelector(
  selectDashboardFeature,
  (state: BdcDashboardState) => state.userVehiclesLoading
);

export const selectUserVehiclesAndAppointmentsLoading = createSelector(
  selectDashboardFeature,
  (state : BdcDashboardState) => { return {vechilesStatus : state.userVehiclesLoading , appointmentStatus : state.appointmentsLoading}}
)

/* To select user vehicles in the dashboard state */
export const selectUserVehicles = createSelector(
  selectDashboardFeature,
  (state: BdcDashboardState) => state.userVehicles
);

// /* To select user vehicles and copy from the dashboard state */
// export const selectUserVehiclesCopy = createSelector(
//   selectUserVehicles,
//   (userVehicles: IVehicle[]) => fromAdapter.copyUserVehiclesProjector
// );

/* ----------------- Appointments -------------------------- */
/* To select the appointments loading flag in the dashboard state */
export const selectAppointmentsLoading = createSelector(
  selectDashboardFeature,
  (state: BdcDashboardState) => state.appointmentsLoading
);

/* To select the appointments loaded flag in the dashboard state */
export const selectAppointmentsLoaded = createSelector(
  selectDashboardFeature,
  (state: BdcDashboardState) => state.appointmentsLoaded
);

/* To select all the appointments in the dashboard state */
export const selectAppointments = createSelector(
  selectDashboardFeature,
  (state: BdcDashboardState) => state.appointments
);

/* To select all the appointments for vin */
export const selectAppointmentsForVin = createSelector(
  selectDashboardFeature,
  (state: BdcDashboardState) => state.appointmentsforvin
);
/* To select only the 'TEST_DRIVE' appointments in the dashboard state */
export const selectTestDriveAppointments = createSelector(
  selectAppointments,
  (appointments: IAppointment[]) =>
    appointments.filter(a => a.appointmentType === AppointmentType.TEST_DRIVE)
);

/* To select only the 'Vehicle Inquiry' appointments in the dashboard state */
export const selectEnquiryAppointments = createSelector(
  selectAppointments,
  (appointments: IAppointment[]) =>
    appointments.filter(a => a.appointmentType === AppointmentType.VEHICLE_INQUIRY)
);

/* To select only the 'Vehicle Inquiry' appointments count in the dashboard state */
export const selectEnquiryAppointmentsCount = createSelector(
  selectEnquiryAppointments,
  (appointments: IAppointment[]) => appointments.length
);

/* To select only the 'TEST_DRIVE' appointments count in the dashboard state */
export const selectTestDriveAppointmentsCount = createSelector(
  selectTestDriveAppointments,
  (appointments: IAppointment[]) => appointments.length
);

/* To select only the 'LEARNING' appointments in the dashboard state */
export const selectLearningAppointments = createSelector(
  selectAppointments,
  (appointments: IAppointment[]) =>
    appointments.filter(a => a.appointmentType === AppointmentType.LEARNING)
);

export const selectAllAppointments = createSelector(
  selectAppointments,
  (appointments: IAppointment[]) =>
    appointments.filter(a => a.appointmentType)
);


export const selectAllAppointmentsCount = createSelector(
  selectAppointments,
  (appointments: IAppointment[]) =>
    appointments.length
);


/* To select only the 'LEARNING' appointments count in the dashboard state */
export const selectLearningAppointmentsCount = createSelector(
  selectLearningAppointments,
  (appointments: IAppointment[]) => appointments.length
);

/* To select only the 'DELIVERY' appointments in the dashboard state */
export const selectDeliveryAppointments = createSelector(
  selectAppointments,
  (appointments: IAppointment[]) =>
    appointments.filter(a => a.appointmentType === AppointmentType.DELIVERY && a.appointmentStatus!=AppointmentStatus.CANCELLING)
);

/* To select only the 'DELIVERY' appointments count in the dashboard state */
export const selectDeliveryAppointmentsCount = createSelector(
  selectDeliveryAppointments,
  (appointments: IAppointment[]) => appointments.length
);

/* To select only the 'SERVICE' appointments in the dashboard state */
export const selectServiceAppointments = createSelector(
  selectAppointments,
  (appointments: IAppointment[]) =>
    appointments.filter(a => a.appointmentType === AppointmentType.SERVICE)
);

/* To select only the 'SERVICE' appointments count in the dashboard state */
export const selectServiceAppointmentsCount = createSelector(
  selectServiceAppointments,
  (appointments: IAppointment[]) => appointments.length
);

/* To select only the 'TEST_DRIVE' appointments in the dashboard state and
* convert it into IDashboardVehicle[] to display in the dashboard */
export const selectTestDriveApptForDashboard = createSelector(
  selectTestDriveAppointments,
  fromAdapter.apptToDashboardVehicleProjector
);

/* To select only the 'Vehicle Inquiry' appointments in the dashboard state and
   convert it into IDashboardVehicle[] to display in the dashboard */
export const selectEnquiryApptForDashboard = createSelector(
  selectEnquiryAppointments,
  fromAdapter.apptToDashboardVehicleProjector
);

/* To select only the 'DELIVERY' appointments in the dashboard state and
* convert it into IDashboardVehicle[] to display in the dashboard */
export const selectDeliveryApptForDashboard = createSelector(
  selectDeliveryAppointments,
  fromAdapter.apptToDashboardVehicleProjector
);

/* To select only the 'Learning' appointments in the dashboard state and
* convert it into IDashboardVehicle[] to display in the dashboard */
export const selectLearningApptForDashboard = createSelector(
  selectLearningAppointments,
  fromAdapter.apptToDashboardVehicleProjector
);

/* To select only the 'Learning' appointments in the dashboard state and
* convert it into IDashboardVehicle[] to display in the dashboard */
export const selectLearningAppointmentsForDashboard = createSelector(
  selectUserVehicles,
  selectLearningAppointments,
  fromAdapter.mergeUserVehiclesAndAppointmentsProjector
);

/* To select only the 'SERVICE' appointments in the dashboard state and
* convert it into IDashboardVehicle[] to display in the dashboard */
export const selectServiceApptForDashboard = createSelector(
  selectUserVehicles,
  selectServiceAppointments,
  fromAdapter.mergeUserVehiclesAndAppointmentsProjector
);

export const selectServiceAppointmentsForDashboard = createSelector(
  selectUserVehicles,
  selectServiceAppointments,
  fromAdapter.getAllVehiclesAppointments
);
/* To select an appointment by appointmentId */
export const selectAppointmentById = createSelector(
  selectAppointments,
  (appointments: IAppointment[], props: { appointmentId: string }) =>
    appointments.find(a => a.appointmentId === props.appointmentId)
);

/* To select the whether an appointment is loaded in the dashboard state by its id */
export const selectAppointmentsLoadedById = createSelector(
  selectAppointmentById,
  (appointment: IAppointment, props: { appointmentId: string }) => !!appointment
);

/* To select whether an appointment is being cancelled by appointmentId */
export const selectIsBeingCancelledByAppointmentId = createSelector(
  selectAppointments,
  (appointments: IAppointment[], props: { appointmentId: string }) =>
    appointments.find(a => a.appointmentId === props.appointmentId)?.appointmentStatus === AppointmentStatus.CANCELLING
);

export const selectUserVehiclesBasedOnVin = createSelector(
  selectServiceApptForDashboard,
  (serviceApptVehicles: IDashboardVehicle[]) =>serviceApptVehicles.filter(dashboardVehicle=>{
    if(dashboardVehicle.vin){
      return dashboardVehicle;
    }
  })

);
export const selectMyUserVehicles = createSelector(
  selectUserVehicles,
  fromAdapter.vehiclesToDashboardVehiclesProjector
);
export const selectMyUserVehiclesCount = createSelector(
  selectMyUserVehicles,
  (vehicles:any[]) => vehicles.length
);

/* pending appointments screen */
export const selectPendingAppointmentState = createSelector(
  selectDashboardFeature,
  (state: BdcDashboardState) => state.pendingAppointments
);

export const selectAllAppointmentState = createSelector(
  selectDashboardFeature,
  (state: BdcDashboardState) => state.appointments
);

export const selectPendingAppointments = createSelector(
  selectPendingAppointmentState,
  (state: IPendingAppointmentState) => state.data
);

export const selectPendingAppointmentsLoading = createSelector(
  selectPendingAppointmentState,
  (state: IPendingAppointmentState) => state.loading
);

export const selectPendingAppointmentsCount = createSelector(
  selectPendingAppointmentState,
  (state: IPendingAppointmentState) => state.allPendingAppointmentsCount
);

export const selectFilteredPendingAppointmentsCount = createSelector(
  selectPendingAppointmentState,
  (state: IPendingAppointmentState) => state.filteredPendingAppointmentsCount
);

export const selectAllApptForDashboard = createSelector(
  selectAllAppointments,
  fromAdapter.apptToDashboardVehicleProjector
);

export const selectAllApptForDashboardCount = createSelector(
  selectDashboardFeature,
  (state: BdcDashboardState) => state.appointments.length
);